import React, { useState, useMemo } from "react";
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import { MESES } from "../constants";

// Colores por tipo
const COLORS = {
  cierres: { bar: "#0b4b1a", line: "#28a745", label: "Cierres" },
  facturacion: { bar: "#0c0c58", line: "#3498db", label: "Facturación" },
};

const formatCompact = (value, pais) => {
  if (pais === "Peru") {
    // Perú: soles en K (miles)
    if (value >= 1_000) return `${Math.round(value / 1_000)}K`;
    return Math.round(value).toString();
  }
  // Chile: pesos en M (millones)
  if (value >= 1_000_000) return `${Math.round(value / 1_000_000)}M`;
  if (value >= 1_000) return `${Math.round(value / 1_000)}K`;
  return Math.round(value).toString();
};

const CustomTooltip = ({ active, payload, label, getMoneda, getDisplayValue, tooltipLabels }) => {
  if (!active || !payload || payload.length === 0) return null;

  return (
    <div className="chart-tooltip">
      <p className="chart-tooltip-title">{label}</p>
      {payload.map((entry) => {
        const meta = tooltipLabels[entry.dataKey];
        if (!meta || !entry.value) return null;
        return (
          <p key={entry.dataKey} className="chart-tooltip-row" style={{ color: meta.color }}>
            <span className="chart-tooltip-dot" style={{ background: meta.color }} />
            {meta.label}: {getMoneda()}
            {getDisplayValue(entry.value)}
          </p>
        );
      })}
    </div>
  );
};

export const ChartModal = ({
  open,
  onClose,
  getDesglose,
  getMoneda,
  getDisplayValue,
  currentMonthIndex,
  pais,
  styles,
}) => {
  // "cierres" = barras son cierres, líneas son facturación
  // "facturacion" = barras son facturación, líneas son cierres
  const [primaryView, setPrimaryView] = useState("cierres");
  const [showSecondary, setShowSecondary] = useState(true);

  const chartData = useMemo(() => {
    if (!getDesglose) return [];

    return MESES.map((mes, i) => {
      const c26 = getDesglose(2026, i + 1, i + 1, "cierres") || {};
      const f26 = getDesglose(2026, i + 1, i + 1, "facturacion") || {};
      const c25 = getDesglose(2025, i + 1, i + 1, "cierres") || {};
      const f25 = getDesglose(2025, i + 1, i + 1, "facturacion") || {};

      const isFutureMonth = i > currentMonthIndex;

      return {
        mes,
        cierres2026: isFutureMonth ? 0 : c26.total || 0,
        facturacion2026: f26.total || 0,
        cierres2025: c25.total || 0,
        facturacion2025: f25.total || 0,
      };
    });
  }, [getDesglose, currentMonthIndex]);

  const isCierresPrimary = primaryView === "cierres";
  const primary = isCierresPrimary ? "cierres" : "facturacion";
  const secondary = isCierresPrimary ? "facturacion" : "cierres";

  const barKey2026 = `${primary}2026`;
  const barKey2025 = `${primary}2025`;
  const lineKey2026 = `${secondary}2026`;
  const lineKey2025 = `${secondary}2025`;

  const tooltipLabels = {
    [barKey2026]: { label: `${COLORS[primary].label} 2026`, color: COLORS[primary].bar },
    [barKey2025]: { label: `${COLORS[primary].label} 2025`, color: COLORS[primary].line },
    [lineKey2026]: { label: `${COLORS[secondary].label} 2026`, color: COLORS[secondary].bar },
    [lineKey2025]: { label: `${COLORS[secondary].label} 2025`, color: COLORS[secondary].line },
  };

  // Promedios para modo solitario
  const avg2025 = useMemo(() => {
    if (chartData.length === 0) return 0;
    const vals = chartData.map((d) => d[barKey2025]).filter(Boolean);
    return vals.length > 0 ? vals.reduce((a, b) => a + b, 0) / 12 : 0;
  }, [chartData, barKey2025]);

  const avg2026 = useMemo(() => {
    if (chartData.length === 0) return 0;
    const vals = chartData.map((d) => d[barKey2026]).filter(Boolean);
    // Para cierres 2026, promediar solo sobre meses transcurridos
    const divisor = primary === "cierres" ? currentMonthIndex + 1 : 12;
    return vals.length > 0 ? vals.reduce((a, b) => a + b, 0) / divisor : 0;
  }, [chartData, barKey2026, primary, currentMonthIndex]);

  if (!open) return null;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div
        className={styles.modalContent}
        onClick={(e) => e.stopPropagation()}
        style={{ width: "85vw", maxWidth: 1350 }}
      >
        {/* Sin título - solo X para cerrar */}
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 4 }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              fontSize: "1.6rem",
              cursor: "pointer",
              color: "#999",
              lineHeight: 1,
              padding: "0 4px",
            }}
          >
            &times;
          </button>
        </div>

        <div className={styles.modalBody}>
          {/* Gráfico */}
          <div style={{ width: "100%", height: "60vh", minHeight: 400 }}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData} margin={{ top: 20, right: 80, left: 10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" vertical={false} />
                <XAxis
                  dataKey="mes"
                  tick={{ fontSize: 12, fill: "#666" }}
                  axisLine={{ stroke: "#ddd" }}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: "#999" }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(v) => formatCompact(v, pais)}
                  width={55}
                />
                <Tooltip
                  content={
                    <CustomTooltip
                      getMoneda={getMoneda}
                      getDisplayValue={getDisplayValue}
                      tooltipLabels={tooltipLabels}
                    />
                  }
                  cursor={{ fill: "rgba(0,0,0,0.04)" }}
                />

                {/* Línea del mes actual */}
                <ReferenceLine
                  x={MESES[currentMonthIndex]}
                  stroke="#c57b1a"
                  strokeDasharray="4 4"
                  strokeWidth={1.5}
                  label={{
                    value: "Hoy",
                    position: "insideTopRight",
                    fill: "#c57b1a",
                    fontSize: 11,
                    fontWeight: 600,
                  }}
                />

                {/* Barras: concepto principal (2025 izquierda, 2026 derecha) */}
                <Bar
                  dataKey={barKey2025}
                  fill={COLORS[primary].line}
                  radius={[3, 3, 0, 0]}
                  barSize={16}
                  opacity={0.45}
                />
                <Bar
                  dataKey={barKey2026}
                  fill={COLORS[primary].bar}
                  radius={[3, 3, 0, 0]}
                  barSize={20}
                  opacity={0.85}
                />

                {/* Líneas: concepto secundario (2026 sólida, 2025 punteada) */}
                {showSecondary && (
                  <Line
                    dataKey={lineKey2026}
                    stroke={COLORS[secondary].bar}
                    strokeWidth={2.5}
                    dot={{ r: 3, fill: COLORS[secondary].bar }}
                    activeDot={{ r: 5 }}
                    type="monotone"
                  />
                )}
                {showSecondary && (
                  <Line
                    dataKey={lineKey2025}
                    stroke={COLORS[secondary].line}
                    strokeWidth={2}
                    strokeDasharray="6 3"
                    dot={{ r: 3, fill: COLORS[secondary].line }}
                    activeDot={{ r: 5 }}
                    type="monotone"
                  />
                )}

                {/* Promedios horizontales: solo en modo solitario */}
                {!showSecondary && avg2025 > 0 && (
                  <ReferenceLine
                    y={avg2025}
                    stroke={COLORS[primary].line}
                    strokeDasharray="8 4"
                    strokeWidth={1}
                    label={{
                      value: `2025: ${formatCompact(avg2025, pais)}`,
                      position: "right",
                      fill: COLORS[primary].line,
                      fontSize: 10,
                    }}
                  />
                )}
                {!showSecondary && avg2026 > 0 && (
                  <ReferenceLine
                    y={avg2026}
                    stroke={COLORS[primary].bar}
                    strokeDasharray="8 4"
                    strokeWidth={1}
                    label={{
                      value: `2026: ${formatCompact(avg2026, pais)}`,
                      position: "right",
                      fill: COLORS[primary].bar,
                      fontSize: 10,
                    }}
                  />
                )}
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          {/* Leyenda dinámica (2025 primero, 2026 después) */}
          <div className="chart-legend">
            <div className="chart-legend-item">
              <span className="chart-legend-bar" style={{ background: COLORS[primary].line, opacity: 0.45 }} />
              {COLORS[primary].label} 2025
            </div>
            <div className="chart-legend-item">
              <span className="chart-legend-bar" style={{ background: COLORS[primary].bar }} />
              {COLORS[primary].label} 2026
            </div>
            {showSecondary && (
              <>
                <div className="chart-legend-item">
                  <span className="chart-legend-line" style={{ borderColor: COLORS[secondary].line }} />
                  {COLORS[secondary].label} 2025
                </div>
                <div className="chart-legend-item">
                  <span
                    className="chart-legend-line"
                    style={{ borderColor: COLORS[secondary].bar, borderStyle: "solid" }}
                  />
                  {COLORS[secondary].label} 2026
                </div>
              </>
            )}
          </div>

          {/* Pills de tipo - toggle exclusivo */}
          <div className="chart-controls" style={{ justifyContent: "center", marginTop: 12, gap: 8 }}>
            <button
              className={`chart-pill ${isCierresPrimary ? "active" : ""}`}
              style={
                isCierresPrimary
                  ? { background: COLORS.cierres.bar, borderColor: COLORS.cierres.bar, color: "#fff" }
                  : {}
              }
              onClick={() => setPrimaryView("cierres")}
            >
              Cierres
            </button>
            <button
              className={`chart-pill ${!isCierresPrimary ? "active" : ""}`}
              style={
                !isCierresPrimary
                  ? { background: COLORS.facturacion.bar, borderColor: COLORS.facturacion.bar, color: "#fff" }
                  : {}
              }
              onClick={() => setPrimaryView("facturacion")}
            >
              Facturación
            </button>
          </div>
          {/* Checkbox comparación debajo */}
          <div style={{ textAlign: "center", marginTop: 6 }}>
            <label
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                fontSize: "0.85rem",
                color: "#666",
                cursor: "pointer",
                userSelect: "none",
              }}
            >
              <input
                type="checkbox"
                checked={showSecondary}
                onChange={() => setShowSecondary((p) => !p)}
                style={{ cursor: "pointer" }}
              />
              Sumar {COLORS[secondary].label}
            </label>
          </div>
        </div>
      </div>
    </div>
  );
};
